import AsyncSelect from "react-select/async";
import { useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Form,
  FormGroup,
  Modal,
  ModalBody,
  Row,
} from "reactstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import EmptyData from "../../../helpers/EmptyData/EmptyData";
import Button from "./../../../components/Button/Button";
import Currency, {
  ConvertCurrency,
} from "./../../../helpers/Currency/Currency";
import { Block } from "./../../../components/Block/Block";
import { dashboardAdminContext } from "../../../context/AdminContext/DashboardAdminContext/DashboardAdminContext";
import { Col } from "./../../../components/Grid/Grid";
import {
  createOrderValidation,
  createProductOrderValidation,
  editOrderValidation,
} from "../../../utils/validation/validation";
import Icon from "../../../components/Icon/Icon";
import ErrorMessage from "./../../../helpers/ErrorMessage/ErrorMessage";
import {
  areYouSureMessage,
  toastErrorMessage,
} from "../../../utils/toastMessage/toastMessage";
import { calculateTotalTableAmount, convertDate } from "../../../utils";
import InfoItem from "./../../../helpers/InfoItem/InfoItem";

const DashboardAdmin = () => {
  const loader = useSelector((state) => state.loader);
  const meta = useSelector((state) => state.meta);

  const {
    order,
    table,
    isProductOrder,
    setIsProductOrder,
    handleShowModal,
    handleHideModal,
    handleGetUsers,
    handleGetProducts,
    handleCreateOrder,
    handleCreateProductOrder,
    handleEditOrder,
    handleDeleteOrder,
    checkExistOrder,
  } = useContext(dashboardAdminContext);

  // Validation
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      isProductOrder
        ? createProductOrderValidation
        : isEmpty(order)
        ? createOrderValidation
        : editOrderValidation
    ),
  });

  const handleChangeTable = (table) => {
    const order = checkExistOrder(table._id);
    if (order.isExist) {
      handleShowModal(table, order.order, setValue);
    } else {
      reset();
      handleShowModal(table);
      setValue("table._id", table._id);
      setValue("table.price", table?.price);
      setValue("table.isVIP", false);
      setValue("table.cue", 0);
      setValue("table.isComplete", false);
    }
  };

  useEffect(() => {
    if (!isEmpty(order))
      setValue(
        "totalTableAmount",
        calculateTotalTableAmount(
          order.startDate,
          +watch("table.price"),
          +watch("table.cue"),
          meta?.cue?.percent
        )
      );
  }, [watch("table.cue"), watch("table.price")]);

  const handleAddProduct = (product) => {
    const products = watch("products") ?? [];
    if (products.some((_p) => _p._id === product._id))
      return toastErrorMessage("محصول تکراری است");

    const initialProduct = {
      _id: product?._id,
      title: product?.title,
      price: product?.price,
      count: 1,
    };
    setValue("products", [...products, initialProduct]);
  };
  const handleRemoveProduct = (index) =>
    setValue(
      "products",
      watch("products")?.filter((_, _i) => _i !== index)
    );

  const totalProductAmount = watch(`products`)?.reduce(
    (prev, current) => (prev += current.price * current.count),
    0
  );
  const totalAmount =
    totalProductAmount +
    (+watch("totalTableAmount") || 0) -
    +watch("deductWallet");

  return (
    <>
      <SiteTitle title="داشبورد" />

      <Block className="mt-4">
        <Button
          color="success"
          size="lg"
          onClick={() => {
            setIsProductOrder(true);
            reset();
          }}
        >
          ثبت سفارش بدون میز
        </Button>
        <hr />
        {isEmpty(meta.tables) ? (
          <EmptyData title="میزی برای نمایش یافت نشد" />
        ) : (
          <Row className="gy-4">
            {meta.tables?.map((item) => {
              const order = checkExistOrder(item._id)?.order;
              const isExist = checkExistOrder(item._id)?.isExist;
              return (
                <Col size="12" md="6" lg="4" key={item._id}>
                  <Card className="text-center h-100">
                    <CardHeader className="h5">
                      {item.title}{" "}
                      {isExist && <Badge color="primary">در حال بازی</Badge>}
                    </CardHeader>
                    <CardBody
                      onClick={() => handleChangeTable(item)}
                      className={`${isExist ? "bg-info" : ""} cursor-pointer`}
                    >
                      <Row className="gy-4">
                        <Col size="6" className={isExist ? "text-white" : ""}>
                          <InfoItem title="قیمت">
                            <ConvertCurrency price={item.price} /> <Currency />
                          </InfoItem>
                        </Col>
                        <Col size="6" className={isExist ? "text-white" : ""}>
                          <InfoItem title="قیمت ویژه">
                            <ConvertCurrency price={item.vipPrice} />{" "}
                            <Currency />
                          </InfoItem>
                        </Col>
                        {!isEmpty(order?.user) && (
                          <>
                            <Col
                              size="12"
                              className={isExist ? "text-white" : ""}
                            >
                              <InfoItem title="مشتری">
                                {order.user?.firstName} {order.user?.lastName} -{" "}
                                {order.user?.phoneNumber}
                              </InfoItem>
                            </Col>
                            <Col
                              size="12"
                              className={isExist ? "text-white" : ""}
                            >
                              <InfoItem title="شروع">
                                {convertDate(
                                  order.startDate,
                                  "YYYY-MM-DD HH:mm"
                                )}
                              </InfoItem>
                            </Col>
                          </>
                        )}
                      </Row>
                    </CardBody>
                    <CardFooter>
                      <Button
                        color={isExist ? "primary" : "success"}
                        className="mx-3"
                        onClick={() => handleChangeTable(item)}
                      >
                        {isExist ? "ویرایش سفارش" : "ثبت سفارش"}
                      </Button>
                      <Button
                        disabled={!isExist}
                        color="danger"
                        className="mx-3"
                        onClick={() =>
                          areYouSureMessage(
                            () => handleDeleteOrder(order._id),
                            `آیا از حذف ${item.title} مطمئنید؟`
                          )
                        }
                      >
                        حذف
                      </Button>
                    </CardFooter>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Block>

      <Modal
        isOpen={!isEmpty(table) || isProductOrder}
        toggle={handleHideModal}
        className="modal-dialog-centered"
        size="lg"
      >
        <ModalBody>
          <span type="button" onClick={handleHideModal} className="close">
            <Icon name="cross-sm" />
          </span>
          <div className="p-2">
            <h5 className="title">
              {isEmpty(order) ? `ثبت سفارش جدید` : `ویرایش سفارش`}
            </h5>
            <br />
            <Form
              className="is-alter"
              onSubmit={handleSubmit(
                isProductOrder
                  ? handleCreateProductOrder
                  : isEmpty(order)
                  ? handleCreateOrder
                  : handleEditOrder
              )}
            >
              <Row className="gy-4">
                <Col md="6">
                  <FormGroup>
                    <label className="form-label" htmlFor="user">
                      مشتری
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        render={({ field: { value, onChange, onBlur } }) => (
                          <>
                            <input type="hidden" className="form-control" />
                            <AsyncSelect
                              openMenuOnFocus
                              cacheOptions
                              className="react-select-container"
                              classNamePrefix="react-select"
                              placeholder="مشتری را انتخاب نمایید"
                              isLoading={loader}
                              onBlur={onBlur}
                              value={value}
                              noOptionsMessage={() => (
                                <p>مشتری برای نمایش یافت نشد</p>
                              )}
                              loadingMessage={() => (
                                <p>لطفا منتظر بمانید ...</p>
                              )}
                              loadOptions={(val, callback) => {
                                handleGetUsers(val, callback);
                              }}
                              defaultOptions
                              onChange={(e) => {
                                onChange(e);
                                const user = e.data;
                                setValue("deductWallet", e.data?.wallet);
                                setValue("table.isVIP", user?.isVIP);
                                setValue(
                                  "table.price",
                                  user?.isVIP ? table.vipPrice : table.price
                                );
                              }}
                            />
                          </>
                        )}
                        name="user"
                      />
                      <ErrorMessage message={errors.user?.message} />
                    </div>
                  </FormGroup>
                </Col>
                {!isEmpty(table) && (
                  <>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="table.isVIP">
                          نوع کاربر
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            render={({
                              field: { onChange, value, onBlur },
                            }) => (
                              <>
                                <input
                                  type="hidden"
                                  className="form-control-lg form-control"
                                />
                                <div className="custom-control custom-checkbox">
                                  <input
                                    type="checkbox"
                                    className="custom-control-input form-control"
                                    checked={value}
                                    onChange={(e) => {
                                      const isVIP = e.target.checked;

                                      onChange(isVIP);
                                      setValue("table.isVIP", isVIP);
                                      setValue(
                                        "table.price",
                                        isVIP ? table.vipPrice : table.price
                                      );
                                    }}
                                    onBlur={onBlur}
                                    id="table.isVIP"
                                  />
                                  <label
                                    className="custom-control-label"
                                    htmlFor="table.isVIP"
                                  >
                                    ویژه
                                  </label>
                                </div>
                              </>
                            )}
                            name="table.isVIP"
                          />
                          <ErrorMessage
                            message={errors.table?.isVIP?.message}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="table.cue">
                          تعداد چوب اضافه
                        </label>
                        <div className="form-control-wrap">
                          <Controller
                            control={control}
                            render={({
                              field: { onChange, value, onBlur },
                            }) => (
                              <>
                                <input
                                  type="hidden"
                                  className="form-control form-control-lg"
                                />
                                <div
                                  className="form-control-wrap number-spinner-wrap"
                                  dir="ltr"
                                >
                                  <Button
                                    color="light"
                                    outline
                                    type="button"
                                    size="lg"
                                    className="btn-icon number-spinner-btn number-minus"
                                    onClick={() => onChange(value + 1)}
                                  >
                                    <Icon name="plus" />
                                  </Button>
                                  <input
                                    type="number"
                                    className="form-control form-control-lg number-spinner bg-white"
                                    value={value}
                                    onChange={(e) =>
                                      onChange(Number(e.target.value))
                                    }
                                    min={0}
                                    onBlur={onBlur}
                                  />
                                  <Button
                                    color="light"
                                    outline
                                    type="button"
                                    size="lg"
                                    disabled={value <= 0}
                                    className="btn-icon number-spinner-btn number-plus"
                                    onClick={() => onChange(value - 1)}
                                  >
                                    <Icon name="minus" />
                                  </Button>
                                </div>
                              </>
                            )}
                            name="table.cue"
                          />
                          <ErrorMessage message={errors.table?.cue?.message} />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="table.price">
                          قیمت میز
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            min={0}
                            placeholder="قیمت میز را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register("table.price")}
                          />
                          <ErrorMessage
                            message={errors.table?.price?.message}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                  </>
                )}
                <Col size="12">
                  <FormGroup>
                    <input type="hidden" className="form-control" />
                    <label className="form-label" htmlFor="products">
                      محصول
                    </label>
                    <AsyncSelect
                      openMenuOnFocus
                      cacheOptions
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="محصول را انتخاب نمایید"
                      isLoading={loader}
                      noOptionsMessage={() => <p>محصولی برای نمایش یافت نشد</p>}
                      loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                      loadOptions={(val, callback) => {
                        handleGetProducts(val, callback);
                      }}
                      defaultOptions
                      onChange={(e) => handleAddProduct(e.data)}
                    />
                    <ErrorMessage message={errors.products?.message} />
                  </FormGroup>
                  {isEmpty(watch("products")) ? (
                    <EmptyData title="محصولی ثبت نشده" />
                  ) : (
                    <>
                      <Row className="gy-4">
                        {watch("products")?.map((item, index) => (
                          <Col size="12" sm="6" lg="4" key={item._id}>
                            <Card className="text-center">
                              <CardHeader className="h-6">
                                {item.title}
                              </CardHeader>
                              <CardBody>
                                <FormGroup>
                                  <label
                                    className="form-label"
                                    htmlFor={`products[${index}].count`}
                                  >
                                    تعداد
                                  </label>
                                  <div className="form-control-wrap">
                                    <Controller
                                      control={control}
                                      render={({
                                        field: { onChange, value, onBlur },
                                      }) => (
                                        <>
                                          <input
                                            type="hidden"
                                            className="form-control form-control-lg"
                                          />
                                          <div
                                            className="form-control-wrap number-spinner-wrap"
                                            dir="ltr"
                                          >
                                            <Button
                                              color="light"
                                              outline
                                              type="button"
                                              size="lg"
                                              className="btn-icon number-spinner-btn number-minus"
                                              onClick={() =>
                                                onChange(value + 1)
                                              }
                                            >
                                              <Icon name="plus" />
                                            </Button>
                                            <input
                                              type="number"
                                              className="form-control form-control-lg number-spinner bg-white"
                                              value={value}
                                              onChange={(e) =>
                                                onChange(Number(e.target.value))
                                              }
                                              min={0}
                                              onBlur={onBlur}
                                            />
                                            <Button
                                              color="light"
                                              outline
                                              type="button"
                                              size="lg"
                                              className="btn-icon number-spinner-btn number-plus"
                                              onClick={() =>
                                                value <= 1
                                                  ? handleRemoveProduct(index)
                                                  : onChange(value - 1)
                                              }
                                            >
                                              <Icon
                                                name={
                                                  value <= 1 ? "trash" : "minus"
                                                }
                                              />
                                            </Button>
                                          </div>
                                        </>
                                      )}
                                      name={`products[${index}].count`}
                                    />
                                    {errors.products && (
                                      <ErrorMessage
                                        message={
                                          errors.products[index]?.count?.message
                                        }
                                      />
                                    )}
                                  </div>
                                </FormGroup>
                              </CardBody>
                              <CardFooter>
                                <ConvertCurrency
                                  price={
                                    item.price *
                                    watch(`products[${index}].count`)
                                  }
                                />{" "}
                                <Currency />
                              </CardFooter>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                      <br />
                      <Alert color="info" className="text-center">
                        جمع کل محصولات:{" "}
                        <ConvertCurrency price={totalProductAmount} />{" "}
                        <Currency />
                      </Alert>
                    </>
                  )}
                </Col>
                {!isEmpty(order) && (
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label" htmlFor="totalTableAmount">
                        مبلغ نهایی میز
                      </label>
                      <div className="form-control-wrap">
                        <input
                          type="number"
                          min={0}
                          placeholder="مبلغ نهایی میز را وارد نمایید"
                          className="form-control-lg form-control"
                          {...register("totalTableAmount")}
                        />
                        <ErrorMessage
                          message={errors.totalTableAmount?.message}
                        />
                      </div>
                    </FormGroup>
                  </Col>
                )}
                <br />
                <Col size="12">
                  <Row className="gy-4">
                    {!isEmpty(table) && (
                      <Col size="12">
                        <Alert color="primary" className="text-center">
                          جمع کل میز و محصولات:{" "}
                          <ConvertCurrency
                            price={
                              totalProductAmount + +watch("totalTableAmount") ||
                              0
                            }
                          />{" "}
                          <Currency />
                        </Alert>
                      </Col>
                    )}
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="deductWallet">
                          کسر از کیف پول کاربر
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            min={0}
                            placeholder="کسر از کیف پول کاربر را وارد نمایید"
                            className="form-control-lg form-control"
                            {...register("deductWallet")}
                          />
                          <ErrorMessage
                            message={errors.deductWallet?.message}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    {!isEmpty(order) && (
                      <Col md="6">
                        <FormGroup>
                          <label className="form-label" htmlFor="isComplete">
                            وضعیت میز
                          </label>
                          <div className="form-control-wrap">
                            <Controller
                              control={control}
                              render={({
                                field: { onChange, value, onBlur },
                              }) => (
                                <>
                                  <input
                                    type="hidden"
                                    className="form-control-lg form-control"
                                  />
                                  <div className="custom-control custom-checkbox">
                                    <input
                                      type="checkbox"
                                      className="custom-control-input form-control"
                                      checked={value}
                                      onChange={(e) =>
                                        onChange(e.target.checked)
                                      }
                                      onBlur={onBlur}
                                      id="isComplete"
                                    />
                                    <label
                                      className="custom-control-label"
                                      htmlFor="isComplete"
                                    >
                                      اتمام میز
                                    </label>
                                  </div>
                                </>
                              )}
                              name="isComplete"
                            />
                            <ErrorMessage
                              message={errors.isComplete?.message}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                    )}
                    <Col size="12">
                      <Alert color="success" className="text-center">
                        جمع کل با کسریات:{" "}
                        <ConvertCurrency
                          price={(totalAmount < 0 ? 0 : totalAmount) || 0}
                        />{" "}
                        <Currency />
                      </Alert>
                    </Col>
                  </Row>
                </Col>
                <Col size="12">
                  <FormGroup>
                    <label className="form-label" htmlFor="description">
                      توضیحات
                    </label>
                    <div className="form-control-wrap">
                      <textarea
                        type="text"
                        placeholder="توضیحات محصول را وارد نمایید"
                        className="form-control-lg form-control"
                        {...register("description")}
                      />
                      <ErrorMessage message={errors.description?.message} />
                    </div>
                  </FormGroup>
                </Col>
                <Col size="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        color="primary"
                        size="lg"
                        type="submit"
                        loading={loader}
                      >
                        {isEmpty(order) ? "ثبت" : "ویرایش"} سفارش
                      </Button>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={handleHideModal}
                        className="link link-light"
                      >
                        انصراف
                      </button>
                    </li>
                  </ul>
                </Col>
              </Row>
            </Form>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default DashboardAdmin;
